import React, { FC, Fragment } from 'react';

import Media from 'react-media';
import { useParams } from 'react-router-dom';

import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import AWARDS from '../../../redux/ducks/awards/constants';
import awardsSelector from '../../../redux/ducks/awards/selectors';
import { useFranchisesQuery } from '../../../redux/ducks/franchises';
import '../../../styles/common/react-table.scss';
import { AwardRecipients, SubNav } from '../../components';

import AwardTombstone from './components/awardTombstone/AwardTombstone';
import TrophySelect from './components/trophySelect';

import './styles.scss';

const mediaQuery = {
    phoneOnly: { maxWidth: 599 },
    tabletPortraitUp: { minWidth: 600 },
    tabletLandscapeUp: { minWidth: 900 },
    desktopUp: { minWidth: 1310 },
};

type Props = {
    awardSlug?: string;
    showHeaders?: boolean;
};

const Award: FC<Props> = ({ awardSlug = '', showHeaders = true }) => {
    const trophySlug = awardSlug || useParams().slug;

    const franchisesResponse = useFranchisesQuery({});
    const {
        awards,
        baseAward,
        baseAwards,
        error: awardError,
        isError: isAwardsError,
        isFetching: isAwardsFetching,
    } = awardsSelector.getAwardsBySlug(trophySlug);

    if (isAwardsError || franchisesResponse.isError) {
        throw awardError || franchisesResponse.error;
    }

    const defaultImage = 'https://nhl.bamcontent.com/images/logos/league/1200x630_NHL.com_FB.JPG';
    const selectedAward = baseAward && { label: baseAward.name, value: baseAward.slug };
    const isWrapClass = (slug) => {
        switch (slug) {
            case AWARDS.LESTER_PATRICK_TROPHY:
            case AWARDS.CONN_SMYTHE_TROPHY:
            case AWARDS.KING_CLANCY_MEMORIAL_TROPHY:
            case AWARDS.EJ_MCGUIRE_AWARD_OF_EXCELLENCE:
            case AWARDS.NHL_LIFETIME_ACHIEVEMENT_AWARD:
            case AWARDS.CLARENCE_S_CAMPBELL_BOWL:
            case AWARDS.BILL_MASTERTON_MEMORIAL_TROPHY:
            case AWARDS.MARK_MESSIER_NHL_LEADERSHIP_AWARD:
            case AWARDS.NHL_FOUNDATION_PLAYER_AWARD:
            case AWARDS.PRINCE_OF_WALES_TROPHY:
            case AWARDS.TED_LINDSAY_AWARD:
            case AWARDS.WILLIE_O_REE_COMMUNITY_HERO_AWARD:
                return 'wrap';
            default:
                return '';
        }
    };

    const awardImage =
        selectedAward?.value === AWARDS.CONN_SMYTHE_TROPHY
            ? awards[0]?.secondaryImageUrl || awards[0]?.imageUrl
            : awards[0]?.imageUrl || awards[0]?.secondaryImageUrl;
    const awardSummary = selectedAward?.value === AWARDS.CONN_SMYTHE_TROPHY ? '' : awards[0]?.summary;

    return (
        <div className="trophy-container">
            <LoadingDisplay isLoading={isAwardsFetching || franchisesResponse.isFetching}>
                {baseAwards.length > 0 && awards?.length > 0 ? (
                    <Fragment>
                        {showHeaders && (
                            <>
                                <SubNav alignLeft>
                                    <TrophySelect trophySlug={trophySlug} />
                                </SubNav>

                                <AwardTombstone baseAward={baseAward} />
                            </>
                        )}

                        <div className={`trophy-overview-container ${isWrapClass(selectedAward.value)}`}>
                            <Media query={mediaQuery.tabletLandscapeUp}>
                                {isWrapClass(selectedAward.value) && (
                                    <div className="trophy-season-overview-container">
                                        <div className="winner-overview-container">
                                            <div className="winner-image">
                                                <img src={awardImage || defaultImage} />
                                            </div>
                                            <div className="winner-description">
                                                <div
                                                    className="winner"
                                                    dangerouslySetInnerHTML={{ __html: awardSummary }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Media>

                            <div className="trophy-winner-historic-table">
                                <AwardRecipients
                                    type={baseAward.trophyCategory.trophyRecipientCategory}
                                    slug={baseAward.slug}
                                    recipients={awards}
                                    franchises={franchisesResponse.data || []}
                                    footnote={baseAward.footnote}
                                />
                            </div>
                        </div>
                    </Fragment>
                ) : null}
            </LoadingDisplay>
        </div>
    );
};

export default Award;
